import React, {useEffect, useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import Dialog from '@material-ui/core/Dialog';
import FormControl from '@material-ui/core/FormControl';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import {toast} from 'react-toastify';
import DialogClose from 'components/DialogClose/styles';
import Loading from 'components/shared/Utils/Loading';
import CheckboxMultiSelect from 'components/select/CheckboxMultiSelect';
import {bulkExportSalesSummaryApi} from 'helpers/services/api/reports';
import {
  BULK_EXPORT_PERIODS,
  BULK_EXPORT_REPORT_TYPES,
  getBulkExportDateRange,
  getBulkExportSubmitError
} from 'helpers/constants/reports/bulkExport';
import PeriodPicker, {
  MENU_PROPS
} from 'routes/App/reports/modals/BulkExport/PeriodPicker';
import {
  BulkExportActions,
  BulkExportCancel,
  BulkExportContainer,
  BulkExportField,
  BulkExportLabel,
  BulkExportRecipient,
  BulkExportSend,
  BulkExportText,
  BulkExportTitle
} from 'routes/App/reports/modals/BulkExport/styles';

const TITLE_ID = 'bulk-export-title';

const SingleSelect = ({labelId, value, options, onChange}) => (
  <div className='Say2EatDrop'>
    <FormControl variant='outlined' fullWidth>
      <Select
        labelId={labelId}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        MenuProps={MENU_PROPS}
      >
        {options.map((option) => (
          <MenuItem
            key={option.value}
            value={option.value}
            className='reportTypeItem'
            disableRipple
          >
            {option.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  </div>
);

SingleSelect.propTypes = {
  labelId: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(PropTypes.object).isRequired,
  onChange: PropTypes.func.isRequired
};

const BulkExportModal = ({
  open,
  locations,
  email,
  timezone,
  onClose,
  onSent
}) => {
  const [reportType, setReportType] = useState(
    BULK_EXPORT_REPORT_TYPES[0].value
  );
  const [selected, setSelected] = useState([]);
  const [period, setPeriod] = useState(BULK_EXPORT_PERIODS.LAST_WEEK);
  const [customRange, setCustomRange] = useState([null, null]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setSelected([]);
      setPeriod(BULK_EXPORT_PERIODS.LAST_WEEK);
      setCustomRange([null, null]);
    }
  }, [open]);

  const locationOptions = useMemo(
    () =>
      (locations || []).map((location) => ({
        value: location._id,
        label: location.FullName || location.BusinessName || location._id
      })),
    [locations]
  );

  const locationIds = selected.length
    ? selected
    : locationOptions.map((option) => option.value);
  const [from, to] = getBulkExportDateRange(period, timezone, customRange);
  const submitError = getBulkExportSubmitError({
    selectedCount: locationIds.length,
    from,
    to
  });

  const handleSend = async () => {
    if (submitError || loading) return;
    setLoading(true);
    try {
      const result = await bulkExportSalesSummaryApi({
        locationIds,
        period: {from, to}
      });
      if (result) onSent(result);
    } catch (error) {
      toast.error(
        error?.response?.data?.errors?.[0]?.message ||
          'Something went wrong. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      scroll='body'
      aria-labelledby={TITLE_ID}
    >
      <BulkExportContainer>
        <DialogClose onClick={onClose} />
        <BulkExportTitle id={TITLE_ID}>Export all locations</BulkExportTitle>
        <BulkExportText>
          Select the report type, the locations and the time period, and we will
          email you all the reports shortly.
        </BulkExportText>

        <BulkExportField>
          <BulkExportLabel id='bulk-export-report-type'>
            Report type
          </BulkExportLabel>
          <SingleSelect
            labelId='bulk-export-report-type'
            value={reportType}
            options={BULK_EXPORT_REPORT_TYPES}
            onChange={setReportType}
          />
        </BulkExportField>

        <BulkExportField>
          <BulkExportLabel id='bulk-export-locations'>
            Locations
          </BulkExportLabel>
          <CheckboxMultiSelect
            labelId='bulk-export-locations-value'
            allLabel={`All locations (${locationOptions.length})`}
            ariaLabel='Locations'
            searchPlaceholder='Search location'
            options={locationOptions}
            selected={selected}
            onChange={setSelected}
          />
        </BulkExportField>

        <BulkExportField>
          <BulkExportLabel id='bulk-export-period'>Time period</BulkExportLabel>
          <PeriodPicker
            labelId='bulk-export-period'
            period={period}
            customRange={customRange}
            timezone={timezone}
            onChange={(nextPeriod, nextRange) => {
              setPeriod(nextPeriod);
              setCustomRange(nextRange);
            }}
          />
        </BulkExportField>

        <BulkExportRecipient>
          Reports will be sent to <strong>{email}</strong>
        </BulkExportRecipient>

        <BulkExportActions>
          <BulkExportCancel onClick={onClose}>Cancel</BulkExportCancel>
          {loading ? (
            <Loading className='LoadingExportButton' />
          ) : (
            <BulkExportSend
              className={submitError ? 'solid disabled' : 'solid'}
              disabled={Boolean(submitError)}
              onClick={handleSend}
            >
              <span>Send report</span>
            </BulkExportSend>
          )}
        </BulkExportActions>
      </BulkExportContainer>
    </Dialog>
  );
};

BulkExportModal.propTypes = {
  open: PropTypes.bool.isRequired,
  locations: PropTypes.arrayOf(PropTypes.object),
  email: PropTypes.string,
  timezone: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onClose: PropTypes.func.isRequired,
  onSent: PropTypes.func.isRequired
};

BulkExportModal.defaultProps = {
  locations: [],
  email: '',
  timezone: 0
};

export default BulkExportModal;
